import React, { useState, useEffect, useCallback } from "react";
import {
  collectionGroup,
  getDocs,
  query,
  where,
  getCountFromServer,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import { COLORS } from "../constants";
import "./Courses.css";

const Courses = () => {
  const [metrics, setMetrics] = useState({
    totalCourses: 0,
    pendingAssignments: 0,
    completedAssignments: 0,
    averageGrade: 0,
    totalAssignments: 0,
    gradedAssignmentsCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadCachedMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load cached metrics from analytics/courses document
      const coursesDocRef = doc(db, "analytics", "courses");
      const coursesDoc = await getDoc(coursesDocRef);

      if (coursesDoc.exists()) {
        const data = coursesDoc.data();
        setMetrics({
          totalCourses: data.totalCourses || 0,
          pendingAssignments: data.pendingAssignments || 0,
          completedAssignments: data.completedAssignments || 0,
          averageGrade: data.averageGrade || 0,
          totalAssignments: data.totalAssignments || 0,
          gradedAssignmentsCount: data.gradedAssignmentsCount || 0,
        });
        setLastUpdated(data.lastUpdated?.toDate() || null);
        console.log(
          "Loaded cached courses metrics from:",
          data.lastUpdated?.toDate()
        );
      } else {
        console.log("No cached metrics found, calculating fresh...");
        await calculateAndSaveMetrics();
      }
    } catch (err) {
      console.error("Error loading cached metrics:", err);
      setError("Failed to load metrics. Please try again.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadCachedMetrics();
  }, [loadCachedMetrics]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await calculateAndSaveMetrics();
    setIsRefreshing(false);
  };

  const calculateAndSaveMetrics = async () => {
    try {
      if (!isRefreshing) {
        setLoading(true);
      }
      setError(null);

      // Use collection group queries to efficiently query across all users
      // This queries all 'courses' collections regardless of parent path
      const coursesRef = collectionGroup(db, "courses");

      // Get total course count using count aggregation
      let totalCourses = 0;
      try {
        const totalCoursesCountSnapshot = await getCountFromServer(coursesRef);
        totalCourses = totalCoursesCountSnapshot.data().count;
        console.log(`Total courses (from count): ${totalCourses}`);
      } catch (countError) {
        console.warn(
          "Count aggregation not available for courses, falling back to getDocs"
        );
        const coursesSnapshot = await getDocs(coursesRef);
        totalCourses = coursesSnapshot.size;
        console.log(`Total courses (from getDocs): ${totalCourses}`);
      }

      // Query all assignments using collection group query
      // This queries all 'assignments' collections regardless of parent path
      const assignmentsRef = collectionGroup(db, "assignments");

      // Get pending assignments (status = false)
      const pendingAssignmentsQuery = query(
        assignmentsRef,
        where("status", "==", false)
      );

      // Get completed assignments (status = true)
      const completedAssignmentsQuery = query(
        assignmentsRef,
        where("status", "==", true)
      );

      let pendingAssignments = 0;
      let completedAssignments = 0;
      let totalGrade = 0;
      let gradedAssignmentsCount = 0;

      // Get pending assignments count
      try {
        const pendingCountSnapshot = await getCountFromServer(
          pendingAssignmentsQuery
        );
        pendingAssignments = pendingCountSnapshot.data().count;
        console.log(`Pending assignments (from count): ${pendingAssignments}`);
      } catch (countError) {
        console.warn(
          "Count aggregation not available for pending assignments, using getDocs"
        );
        const pendingSnapshot = await getDocs(pendingAssignmentsQuery);
        pendingAssignments = pendingSnapshot.size;
        console.log(
          `Pending assignments (from getDocs): ${pendingAssignments}`
        );
      }

      // Get completed assignments - we need to read them anyway for grade calculation
      // So we'll use getDocs and get both count and grades in one pass
      const completedSnapshot = await getDocs(completedAssignmentsQuery);
      completedAssignments = completedSnapshot.size;
      console.log(`Completed assignments: ${completedAssignments}`);

      // Calculate average grade from completed assignments
      completedSnapshot.forEach((assignmentDoc) => {
        const assignmentData = assignmentDoc.data();
        // Check if assignment has a grade field and it's a valid number
        if (
          assignmentData.grade !== undefined &&
          assignmentData.grade !== null &&
          typeof assignmentData.grade === "number"
        ) {
          totalGrade += assignmentData.grade;
          gradedAssignmentsCount++;
        }
      });

      const totalAssignments = pendingAssignments + completedAssignments;
      const averageGrade =
        gradedAssignmentsCount > 0 ? totalGrade / gradedAssignmentsCount : 0;

      console.log("=== Courses Metrics Summary ===");
      console.log(`Total courses: ${totalCourses}`);
      console.log(`Total assignments: ${totalAssignments}`);
      console.log(`Pending assignments: ${pendingAssignments}`);
      console.log(`Completed assignments: ${completedAssignments}`);
      console.log(`Graded assignments: ${gradedAssignmentsCount}`);
      console.log(`Average grade: ${averageGrade.toFixed(2)}%`);

      const metricsData = {
        totalCourses,
        pendingAssignments,
        completedAssignments,
        averageGrade,
        totalAssignments,
        gradedAssignmentsCount,
        lastUpdated: new Date(),
      };

      setMetrics(metricsData);
      setLastUpdated(new Date());

      // Save metrics to analytics/courses document
      const coursesDocRef = doc(db, "analytics", "courses");
      await setDoc(coursesDocRef, {
        ...metricsData,
        lastUpdated: new Date(),
      });

      console.log("✅ Courses metrics saved to analytics/courses");
    } catch (err) {
      console.error("Error calculating courses metrics:", err);
      setError("Failed to calculate courses metrics. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="courses-page">
        <div className="page-header">
          <h1>Courses</h1>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading courses data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="courses-page">
        <div className="page-header">
          <h1>Courses</h1>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={handleRefresh} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const formatLastUpdated = () => {
    if (!lastUpdated) return "";

    const now = new Date();
    const diffMs = now - lastUpdated;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60)
      return `${diffMins} minute${diffMins === 1 ? "" : "s"} ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  };

  // Prepare pie chart data for assignment status
  const pieData = [
    {
      name: "Pending",
      value: metrics.pendingAssignments,
      color: COLORS.warning,
    },
    {
      name: "Completed",
      value: metrics.completedAssignments,
      color: COLORS.success,
    },
  ];

  const pieColors = [COLORS.warning, COLORS.success];

  return (
    <div className="courses-page">
      <div className="page-header">
        <div>
          <h1>Courses</h1>
          {lastUpdated && (
            <p className="last-updated">Last updated: {formatLastUpdated()}</p>
          )}
        </div>
        <button
          onClick={handleRefresh}
          className="refresh-button"
          disabled={isRefreshing}
        >
          {isRefreshing ? "⏳ Refreshing..." : "🔄 Refresh"}
        </button>
      </div>

      <div className="courses-grid">
        <div className="courses-card full-width">
          <h3 className="card-title">Overview</h3>
          <div className="courses-overview">
            <div className="overview-metric">
              <div
                className="metric-value large"
                style={{ color: COLORS.primary }}
              >
                {metrics.totalCourses}
              </div>
              <div className="metric-label">Total Courses</div>
            </div>
            <div className="overview-metric">
              <div className="metric-value" style={{ color: COLORS.info }}>
                {metrics.totalAssignments}
              </div>
              <div className="metric-label">Total Assignments</div>
            </div>
            <div className="overview-metric">
              <div className="metric-value" style={{ color: COLORS.warning }}>
                {metrics.pendingAssignments}
              </div>
              <div className="metric-label">Pending</div>
            </div>
            <div className="overview-metric">
              <div className="metric-value" style={{ color: COLORS.success }}>
                {metrics.completedAssignments}
              </div>
              <div className="metric-label">Completed</div>
            </div>
          </div>
        </div>

        <div className="courses-card">
          <h3 className="card-title">Assignment Status</h3>
          {metrics.totalAssignments > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent, value }) => {
                    if (percent < 0.05) return "";
                    return `${name}: ${value} (${(percent * 100).toFixed(0)}%)`;
                  }}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={pieColors[index] || entry.color}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  formatter={(value) => {
                    const item = pieData.find((d) => d.name === value);
                    return item ? `${value} (${item.value})` : value;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-chart">
              <p>No assignments found</p>
            </div>
          )}
        </div>

        <div className="courses-card">
          <h3 className="card-title">Average Grade</h3>
          <div className="grade-display">
            <div className="grade-value" style={{ color: COLORS.primary }}>
              {metrics.averageGrade > 0
                ? `${metrics.averageGrade.toFixed(1)}%`
                : "N/A"}
            </div>
            <div className="grade-count" style={{ color: COLORS.gray[600] }}>
              {metrics.gradedAssignmentsCount > 0
                ? `${metrics.gradedAssignmentsCount} assignment${
                    metrics.gradedAssignmentsCount !== 1 ? "s" : ""
                  } graded`
                : ""}
            </div>
            <p className="grade-description">
              {metrics.averageGrade > 0
                ? "Average across all graded assignments"
                : "No graded assignments yet"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Courses;
